import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import {
  parseRailwayJson,
  railwayOutputTail,
  runRailway
} from '@next-adapter/init/railway/runner.js'
import type {
  RailwayProjectContext,
  RailwayProjectSummary,
  RailwayRunner,
  RailwayService,
  RailwayWorkspace
} from '@next-adapter/init/railway/types.js'
import pc from 'picocolors'

interface RailwayStatusEnvironment {
  id: string
  name: string
}

interface RailwayStatusPayload {
  id: string
  name: string
  environments?: {
    edges?: Array<{ node?: Partial<RailwayStatusEnvironment> }>
  }
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function hasNoUsableLinkedProject(detail: string): boolean {
  return /no linked project found|no project specified.*railway link|project is deleted.*railway link/i.test(
    detail
  )
}

function isDeletedRailwayProject(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const deletedAt = (value as Record<string, unknown>).deletedAt
  return isString(deletedAt)
}

function parseProjectSummary(value: unknown): RailwayProjectSummary | undefined {
  if (!value || typeof value !== 'object') return undefined
  const project = value as Record<string, unknown>
  if (!isString(project.id) || !isString(project.name)) return undefined

  const workspaceValue = project.workspace
  let workspace: RailwayWorkspace | undefined
  if (workspaceValue && typeof workspaceValue === 'object') {
    const candidate = workspaceValue as Record<string, unknown>
    if (isString(candidate.id) && isString(candidate.name)) {
      workspace = { id: candidate.id, name: candidate.name }
    }
  }

  return { id: project.id, name: project.name, workspace }
}

/** Match Vercel's fresh-project behavior without reusing prior provider resources. */
const MAX_PROJECT_NAME_ATTEMPTS = 10

export function versionedRailwayProjectName(base: string, attempt: number): string {
  return attempt <= 1 ? base : `${base}-v${attempt}`
}

function railwayProjectNameIsTaken(projects: RailwayProjectSummary[], candidate: string): boolean {
  return projects.some((project) => project.name.toLowerCase() === candidate.toLowerCase())
}

function isRailwayProjectNameConflict(detail: string): boolean {
  return /already exists|name.*taken|duplicate.*name/i.test(detail)
}

export async function readRailwayProjectContext(
  runner: RailwayRunner,
  cwd: string,
  environment: string,
  env?: NodeJS.ProcessEnv
): Promise<RailwayProjectContext | undefined> {
  const result = await runRailway(runner, ['status', '--environment', environment, '--json'], {
    cwd,
    mode: 'capture',
    env
  })
  if (!result.success) {
    const detail = railwayOutputTail(`${result.stdout}\n${result.stderr}`)
    if (detail && hasNoUsableLinkedProject(detail)) {
      return undefined
    }
    throw new Error(detail ?? 'Could not inspect the linked Railway project.')
  }

  const payload = parseRailwayJson<RailwayStatusPayload>(result.stdout)
  if (!payload || !isString(payload.id) || !isString(payload.name)) {
    throw new Error('Railway returned an invalid project status response.')
  }

  const environments = payload.environments?.edges ?? []
  const selected = environments
    .map((edge) => edge.node)
    .find(
      (candidate) =>
        isString(candidate?.id) &&
        isString(candidate?.name) &&
        (candidate.id.toLowerCase() === environment.toLowerCase() ||
          candidate.name.toLowerCase() === environment.toLowerCase())
    )

  if (!selected || !isString(selected.id) || !isString(selected.name)) {
    throw new Error(
      `Railway project ${payload.name} does not have an environment matching "${environment}".`
    )
  }

  return {
    id: payload.id,
    name: payload.name,
    environmentId: selected.id,
    environmentName: selected.name
  }
}

async function listRailwayProjects(
  runner: RailwayRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<RailwayProjectSummary[]> {
  const result = await runRailway(runner, ['project', 'list', '--json'], {
    cwd,
    mode: 'capture',
    env
  })
  if (!result.success) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ?? 'Could not list Railway projects.'
    )
  }

  const payload = parseRailwayJson<unknown>(result.stdout)
  if (!Array.isArray(payload)) {
    throw new Error('Railway returned an invalid project list response.')
  }

  const projects = payload
    .filter((project) => !isDeletedRailwayProject(project))
    .map(parseProjectSummary)
  if (projects.some((project) => !project)) {
    throw new Error('Railway returned an invalid project list response.')
  }

  return projects as RailwayProjectSummary[]
}

async function linkRailwayProject(
  runner: RailwayRunner,
  cwd: string,
  project: string,
  environment: string,
  options: { workspace?: string; service?: string; env?: NodeJS.ProcessEnv } = {}
): Promise<void> {
  const args = ['project', 'link', '--project', project, '--environment', environment, '--json']
  if (options.workspace) args.push('--workspace', options.workspace)
  if (options.service) args.push('--service', options.service)

  const result = await runRailway(runner, args, { cwd, mode: 'capture', env: options.env })
  if (!result.success) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ??
        `Could not link Railway project ${project}.`
    )
  }
}

async function createRailwayProject(
  runner: RailwayRunner,
  cwd: string,
  projectName: string,
  existingProjects: RailwayProjectSummary[],
  options: { workspace?: string; env?: NodeJS.ProcessEnv }
): Promise<RailwayProjectSummary> {
  const knownProjects = [...existingProjects]

  for (let attempt = 1; attempt <= MAX_PROJECT_NAME_ATTEMPTS; attempt++) {
    const candidate = versionedRailwayProjectName(projectName, attempt)
    if (railwayProjectNameIsTaken(knownProjects, candidate)) continue

    const args = ['init', '--name', candidate, '--json']
    if (options.workspace) args.push('--workspace', options.workspace)

    const result = await runRailway(runner, args, { cwd, mode: 'capture', env: options.env })
    const project = result.success
      ? parseProjectSummary(parseRailwayJson<unknown>(result.stdout))
      : undefined
    if (project) return project

    const detail = railwayOutputTail(`${result.stdout}\n${result.stderr}`)
    if (!detail || !isRailwayProjectNameConflict(detail)) {
      throw new Error(detail ?? `Could not create Railway project ${candidate}.`)
    }

    knownProjects.push({ id: candidate, name: candidate })
  }

  throw new Error(
    `Could not create a unique Railway project name after ${MAX_PROJECT_NAME_ATTEMPTS} attempts.`
  )
}

export async function ensureRailwayProject(
  runner: RailwayRunner,
  options: {
    cwd: string
    projectName: string
    interactive: boolean
    project?: string
    workspace?: string
    workspaces?: RailwayWorkspace[]
    environment?: string
    env?: NodeJS.ProcessEnv
  }
): Promise<RailwayProjectContext> {
  const environment = options.environment?.trim() || 'production'
  const projectSpinner = spinner()
  projectSpinner.start('Checking Railway project')

  try {
    if (options.project) {
      await linkRailwayProject(runner, options.cwd, options.project, environment, {
        workspace: options.workspace,
        env: options.env
      })
    } else {
      let createWorkspace = options.workspace
      if (!createWorkspace && options.workspaces?.length === 1) {
        createWorkspace = options.workspaces[0]?.id
      } else if (!createWorkspace && options.interactive && options.workspaces?.length) {
        projectSpinner.clear()
        const workspace = await p.select({
          message: 'Choose a Railway workspace',
          options: options.workspaces.map((candidate) => ({
            value: candidate.id,
            label: candidate.name
          }))
        })
        if (p.isCancel(workspace)) {
          p.cancel('Setup cancelled.')
          process.exit(0)
        }
        createWorkspace = workspace
        projectSpinner.start('Checking Railway project names')
      }

      const projects = await listRailwayProjects(runner, options.cwd, options.env)
      projectSpinner.message(`Creating Railway project ${pc.cyan(options.projectName)}`)
      const created = await createRailwayProject(
        runner,
        options.cwd,
        options.projectName,
        projects,
        {
          workspace: createWorkspace,
          env: options.env
        }
      )

      await linkRailwayProject(runner, options.cwd, created.id, environment, {
        workspace: created.workspace?.id ?? createWorkspace,
        env: options.env
      })

      const context = await readRailwayProjectContext(runner, options.cwd, environment, options.env)
      if (!context || context.id !== created.id) {
        throw new Error('Railway did not link the newly created project.')
      }
      projectSpinner.clear()
      return context
    }

    const context = await readRailwayProjectContext(runner, options.cwd, environment, options.env)
    projectSpinner.clear()
    if (!context) {
      throw new Error(`Railway project is linked, but environment "${environment}" is unavailable.`)
    }
    return context
  } finally {
    projectSpinner.clear()
  }
}

export async function linkRailwayService(
  runner: RailwayRunner,
  cwd: string,
  project: RailwayProjectContext,
  service: RailwayService,
  env?: NodeJS.ProcessEnv
): Promise<void> {
  await linkRailwayProject(runner, cwd, project.id, project.environmentName, {
    service: service.id,
    env
  })
}
